import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'

export type ResearchRole = 'business_owner' | 'seo_specialist'
export type ResearchStatus = 'pending' | 'approved' | 'declined' | 'revoked'

/** One person who applied to the research program. `staff_note` is private: applicants never see it. */
export interface ResearchApplication {
  id: string
  name: string
  email: string
  role: ResearchRole
  /** Empty on applications made before the question was added. */
  business_name: string | null
  websites: string[]
  website_access: 'own' | 'client_permission'
  country: string
  platform: string
  industry: string
  experience: 'beginner' | 'some' | 'experienced'
  goals: string
  status: ResearchStatus
  staff_note: string | null
  decided_by: string | null
  decided_at: string | null
  invited_at: string | null
  invite_count: number
  account_created_at: string | null
  account_verified_at: string | null
  grant_applied_at: string | null
  workspace: { id: string; name: string } | null
  terms_version: string
  terms_accepted_at: string
  created_at: string | null
}

export interface ResearchPlaces {
  taken: number
  slots: number
  pending: number
}

export interface ResearchMeta {
  places: Record<ResearchRole, ResearchPlaces>
  can_decide: boolean
  invite_subject: string
  open: boolean
}

export interface ResearchList {
  data: ResearchApplication[]
  meta: ResearchMeta
}

const key = ['research-program'] as const

export function useResearchProgram() {
  return useQuery({ queryKey: key, queryFn: () => http.get<ResearchList>('/research-program') })
}

type Action = 'approve' | 'decline' | 'resend' | 'revoke'

/** Approve, decline, resend the invite or revoke; the list is fetched again afterwards. */
export function useResearchAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      action,
      ...body
    }: {
      id: string
      action: Action
      note?: string | null
      notify?: boolean
    }) =>
      http.post<{ data: ResearchApplication; meta: ResearchMeta }>(`/research-program/${id}/${action}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  })
}
